import { useCallback, useEffect, useRef } from "react";
import type { RefObject } from "react";

export type RefMap = Record<string, RefObject<HTMLElement | null>>;

interface UseSpotlightResult {
  spotlight: (key: string) => void;
}

const SPOTLIGHT_MS = 4000;
const PENDING_KEY = "paperchase-guide-pending-spotlight";
const PENDING_RETRY_MS = 100;
const PENDING_MAX_MS = 2000;

// Stable empty map — avoids a new object identity each render when no refMap is passed,
// which would cause `spotlight` (memoized on [refMap]) to change every render and
// re-trigger the pending-spotlight useEffect.
const EMPTY_REF_MAP: RefMap = {};

export function useSpotlight(refMap: RefMap = EMPTY_REF_MAP): UseSpotlightResult {
  const activeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const spotlight = useCallback(
    (key: string) => {
      // Try refMap first, then fall back to data-guide-target querySelector
      const ref = refMap[key];
      const element: HTMLElement | null =
        ref?.current ??
        document.querySelector<HTMLElement>(`[data-guide-target="${key}"]`);
      if (!element) return;

      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.setAttribute("data-spotlight", "");

      if (activeTimeoutRef.current) {
        clearTimeout(activeTimeoutRef.current);
      }

      activeTimeoutRef.current = setTimeout(() => {
        element.removeAttribute("data-spotlight");
        activeTimeoutRef.current = null;
      }, SPOTLIGHT_MS);
    },
    [refMap],
  );

  // Consume pending-spotlight key left by a prior route's Show Me action.
  // Retries for up to 2 seconds so the destination DOM has time to mount.
  useEffect(() => {
    const pending = sessionStorage.getItem(PENDING_KEY);
    if (!pending) return;
    sessionStorage.removeItem(PENDING_KEY);

    const startedAt = Date.now();
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tryFire = () => {
      if (cancelled) return;
      const el = document.querySelector<HTMLElement>(
        `[data-guide-target="${pending}"]`,
      );
      if (el) {
        spotlight(pending);
        return;
      }
      if (Date.now() - startedAt < PENDING_MAX_MS) {
        timer = setTimeout(tryFire, PENDING_RETRY_MS);
      }
    };
    tryFire();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [spotlight]);

  return { spotlight };
}
